import { ArrowLeftIcon, Download } from "@/components/icons";
import { Button, Skeleton, toast } from "@/components/kit";
import { buildOrderSummaryCardViewModel } from "@/modules/chat-tools/domain/build-order-summary-card";
import { useShopSettingsQuery } from "@/modules/chat-tools/presentation/api/queries";
import { downloadElementAsPng } from "@/modules/chat-tools/presentation/lib/download-label-image";
import { OrderSummaryCard } from "@/modules/chat-tools/presentation/ui/OrderSummaryCard";
import { useOrderQuery } from "@/modules/orders/presentation/api/queries";
import { useNavigate, useParams } from "@tanstack/react-router";
import { useRef, useState } from "react";

export function OrderSummaryImagePage() {
  const { id } = useParams({ from: "/app/orders/$id/summary-image" });
  const nav = useNavigate();
  const cardRef = useRef<HTMLDivElement>(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const orderQuery = useOrderQuery(id);
  const settingsQuery = useShopSettingsQuery();
  const order = orderQuery.data;
  const settings = settingsQuery.data;

  const isLoading = orderQuery.isLoading || settingsQuery.isLoading;

  async function handleDownload() {
    const element = cardRef.current;
    if (!element || !order) return;

    setIsDownloading(true);
    try {
      const safeName = order.orderNumber.replace(/[^\w-]+/g, "-");
      await downloadElementAsPng(element, `${safeName}-order-summary.png`);
      toast.success("ດາວໂຫລດຮູບສະຫຼຸບແລ້ວ");
    } catch (e) {
      toast.error("ດາວໂຫລດບໍ່ສຳເລັດ", {
        description: e instanceof Error ? e.message : undefined,
      });
    } finally {
      setIsDownloading(false);
    }
  }

  if (isLoading) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-background">
        <Skeleton className="h-128 w-full max-w-xs rounded-3xl" />
      </div>
    );
  }

  if (!order || !settings) {
    return (
      <div className="fixed inset-0 z-50 flex flex-col items-center justify-center gap-4 bg-background">
        <p className="text-muted-foreground">ບໍ່ພົບອໍເດີ້</p>
        <Button
          variant="outline"
          className="rounded-full no-print"
          onClick={() => nav({ to: "/app/orders" })}
        >
          ກັບໄປລາຍການ
        </Button>
      </div>
    );
  }

  const summary = buildOrderSummaryCardViewModel(order, settings);

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-background text-foreground">
      <div className="no-print flex items-center justify-between gap-3 border-border border-b bg-background px-4 py-3">
        <Button
          variant="outline"
          className="rounded-full"
          onClick={() => nav({ to: "/app/orders/$id", params: { id } })}
        >
          <ArrowLeftIcon data-icon="inline-start" />
          ກັບຄືນ
        </Button>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            className="rounded-full"
            isLoading={isDownloading}
            onClick={handleDownload}
          >
            <Download data-icon="inline-start" />
            ດາວໂຫລດຮູບ
          </Button>
          <Button
            variant="outline"
            className="rounded-full"
            onClick={() => window.print()}
          >
            ພິມ
          </Button>
        </div>
      </div>

      <div className="flex flex-1 items-start justify-center overflow-y-auto p-4 sm:items-center sm:p-6 print:p-0">
        <OrderSummaryCard
          ref={cardRef}
          id="order-summary-print"
          summary={summary}
        />
      </div>
    </div>
  );
}
